/**
 * InstructorBadgeRow - Renders instructor + collaborator profile badges
 * Shared component extracted from duplicated rendering across class card variants.
 */

import { ProfileBadge } from "@/components/ui/profile-badge";
import type { InstructorDisplayInfo } from "@/lib/utils/class-instructor-utils";
import type { CollaboratorProfileInfo } from "@/lib/api/classes";
import { getSubstituteSuffix } from "@/lib/utils/class-instructor-utils";
import {
  getWithProfiles,
  shouldShowWithBadge,
} from "@/lib/utils/class-collaborator-utils";
import { cn } from "@/lib/utils/general";

interface InstructorBadgeRowProps {
  instructor: InstructorDisplayInfo;
  collaborators: CollaboratorProfileInfo[];
  isCollaborativeClass: boolean;
  ownerProfile?: CollaboratorProfileInfo | null;
  variant: "light" | "default";
  /** Show the main instructor badge. Default true. When false, collaborator badges still show with "with" prefix. */
  showInstructor?: boolean;
}

export function InstructorBadgeRow({
  instructor,
  collaborators,
  isCollaborativeClass,
  ownerProfile,
  variant,
  showInstructor = true,
}: InstructorBadgeRowProps) {
  const showWithBadge = shouldShowWithBadge(
    isCollaborativeClass,
    ownerProfile,
    collaborators,
  );
  const withProfiles = showWithBadge
    ? getWithProfiles(isCollaborativeClass, ownerProfile, collaborators)
    : [];

  // If neither instructor nor collaborators should be shown, render nothing
  if (!showInstructor && !showWithBadge) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1">
      {showInstructor && (
        <ProfileBadge
          username={instructor.username}
          fullName={instructor.name}
          profilePhoto={instructor.photo}
          showAvatar={instructor.hasProfile}
          suffix={getSubstituteSuffix(instructor.isSubstitute)}
          variant={variant}
        />
      )}
      {showWithBadge && (
        <>
          <span
            className={cn(
              "text-sm",
              variant === "light" ? "text-white/80" : "text-on-dark-muted",
            )}
          >
            {showInstructor ? "&" : "with"}
          </span>
          {withProfiles.map((collab, index) => (
            <span key={collab.id} className="inline-flex items-center gap-1">
              {index > 0 && (
                <span
                  className={cn(
                    "text-sm -ml-1",
                    variant === "light"
                      ? "text-white/80"
                      : "text-on-dark-muted",
                  )}
                >
                  &
                </span>
              )}
              <ProfileBadge
                username={collab.username}
                fullName={collab.full_name}
                profilePhoto={collab.profile_photo}
                variant={variant}
              />
            </span>
          ))}
        </>
      )}
    </div>
  );
}
